import { useMemo, useRef, useState } from 'react';

import { Box, IconButton, Slide } from '@mui/material';
import { hasActiveModuleGrant } from '@realadvisor/constants';
import { X } from 'lucide-react';

import type { GetLeadDetailsQuery } from '@graphql/__generated__/graphql';
import { LoadingSpinner } from '@modules/_shared/components/LoadingSpinner';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { CONTENT_HEIGHT } from '@modules/_shared/components/TwoPaneLayout';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { LeadDetailsCardStack } from '@modules/lead-details/components/LeadDetailsCardStack';
import LeadFeedV2 from '@modules/lead-details/components/LeadFeedV2';
import { useCmaPreview } from '@modules/lead-details/hooks/useCmaPreview';
import type { LeadPermissions } from '@modules/lead-details/hooks/useLeadPermissions';
import { LeadDetailsMap } from '@modules/lead-details/LeadDetailsMap';
import { getLeadMapProperty } from '@modules/lead-details/utils/getLeadMapProperty';
import CMAReportEditorWorkflow from '@modules/reports/cma-reports/CMAReportEditor/CMAReportEditorWorkflow';
import { MODULES_BROKERAGE } from '@shared/constants/module';

type Lead = NonNullable<GetLeadDetailsQuery['leads_by_pk']>;

type MobileLeadDetailsLayoutProps = {
  lead: Lead;
  leadId: string;
  permissions: LeadPermissions;
  refetch: () => Promise<unknown>;
  onAssignmentChange: (keepOpen: boolean) => void;
};

export const MobileLeadDetailsLayout = ({
  lead,
  leadId,
  permissions,
  refetch,
  onAssignmentChange,
}: MobileLeadDetailsLayoutProps) => {
  const { canView, isAdmin, restrictedAgentView } = permissions;
  const { me } = useAppData();
  const isBrokerageModuleIncluded = hasActiveModuleGrant(
    me?.module_grants,
    MODULES_BROKERAGE,
  );
  const leadAgents = useMemo(() => lead.lead_agents ?? [], [lead.lead_agents]);
  const mapProperty = getLeadMapProperty(lead);
  const requiresAgentSelection = isAdmin && leadAgents.length > 1;
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);

  const feedScrollRef = useRef<HTMLDivElement>(null);

  const {
    cmaReport,
    isPreviewOpen,
    isLoading: cmaPreviewLoading,
    apolloError,
    openPreview,
    closePreview,
    clearError,
  } = useCmaPreview();

  return (
    <>
      <Box
        ref={feedScrollRef}
        sx={{
          overflowY: 'auto',
          height: CONTENT_HEIGHT,
          pb: 2,
        }}
      >
        {mapProperty && (
          <LeadDetailsMap
            key={canView ? 'full' : 'preview'}
            property={mapProperty}
            initialTab={lead.potential_development ? 'zoning' : 'satellite'}
            previewMode={!canView}
            actionsEnabled={canView && !restrictedAgentView}
          />
        )}
        <Box sx={{ minWidth: 0, my: 2 }}>
          <LeadDetailsCardStack
            lead={lead}
            leadId={leadId}
            isAdmin={isAdmin}
            isBrokerageModuleIncluded={isBrokerageModuleIncluded}
            restrictedAgentView={restrictedAgentView}
            canView={canView}
            selectedAgentId={selectedAgentId}
            setSelectedAgentId={setSelectedAgentId}
            requiresAgentSelection={requiresAgentSelection}
            refetch={refetch}
            onAssignmentChange={onAssignmentChange}
            onCmaPreviewGenerated={openPreview}
            isPreviewOpen={isPreviewOpen}
            onClosePreview={closePreview}
          />
        </Box>
        <LeadFeedV2
          lead_id={lead.id}
          canView={canView}
          scrollParentRef={feedScrollRef}
        />
      </Box>

      <Slide direction="up" in={isPreviewOpen} timeout={400} unmountOnExit>
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'background.paper',
            zIndex: theme => theme.zIndex.modal,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              p: 1,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <IconButton
              onClick={closePreview}
              size="small"
              sx={{
                bgcolor: 'grey.100',
                '&:hover': { bgcolor: 'grey.200' },
                transition: 'background-color 0.15s ease',
              }}
            >
              <X size={18} />
            </IconButton>
          </Box>

          {cmaPreviewLoading && <LoadingSpinner sx={{ flexGrow: 1 }} />}
          {!cmaPreviewLoading && cmaReport && (
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              <CMAReportEditorWorkflow cmaReport={cmaReport} previewOnly />
            </Box>
          )}
        </Box>
      </Slide>

      <MutationErrorSnackbar error={apolloError} onClose={clearError} />
    </>
  );
};
